import {
    AmountValueFormat,
    ApplicationDataArray,
    ApplicationDataObject,
    BarcodeType,
    CarouselStyle,
    ControlRegexValidator,
    ControlSize,
    ControlSubField,
    ControlType,
    ControlValueOption,
    CybersourceEnvironment,
    DefaultPermissionsTo,
    DynamicOptionSource,
    FileType,
    GeolocationDisplayStyle,
    IApplicationDataObject,
    ICarouselControl,
    IContainerControl,
    IControl,
    IControlGeoLocation,
    IControlPrePopulate,
    IControlWithBarCode,
    IControlWithDates,
    IControlWithDefaultValue,
    IControlWithLabel,
    IControlWithObjectData,
    IControlWithOptions,
    IControlWithPlaceholder,
    IControlWithRows,
    IControlWithSize,
    IControlWithToolTip,
    IControlWithValidators,
    IFileBrowserControl,
    IFormControl,
    IIndexableControl,
    InputType,
    IOptionSource,
    isControlWithOptions,
    ISearchControl,
    IValueControl,
    IValuePersistableControl,
    LikertScaleItem,
    MobileKeyboardOptions,
    OptionSourceType,
    PaymentGateway,
    PaymentGatewayEnvironment,
    PhotoOrientation,
    PhotoSize,
    RepeaterDisplayStyle,
    RepeaterOptionSource,
    StaticOptionSource,
} from './control.interface';
import { ControlFactory } from './control-factory';
import { VisibilityTruthCondition } from './truth-condition.model';
import { AccessLevel, MappedField, MapType } from './common.model';
import { trySetProp } from '../global.functions';
import { BehaviorSubject, Observable } from 'rxjs';
import { ServiceEndpointUtilisation } from './workflow.model';

export class Control implements IControl {
    /** Gets or sets the control id. */
    id!: number;
    /** Gets or sets the control name. */
    name!: string;
    /** Gets or sets the Control Type. */
    type!: ControlType;
    /** Gets or sets the position on the page. */
    sortPosition!: number;
    /** Gets or sets the parent control id. */
    parentControlId!: number | undefined;
    /** Gets or sets control notes.
        The Notes property is available in the Workbench only and can be used to record comments or explanations. */
    notes!: string | undefined;

    visibilityRule: VisibilityTruthCondition | undefined;

    hasVisibilityRule: boolean = false;

    access: AccessLevel = AccessLevel.NoAccess;

    // Custom internal type name for display purposes
    typeName: string | undefined;

    pageId: number;

    pageIndex: number;

    constructor(data?: IControl) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.id = _data['Id'];  // _data['_id'] ? _data['_id']['$oid'] : <any>undefined;
            this.name = _data['Name'];
            this.sortPosition = _data['SortPosition'];
            this.parentControlId = _data['ParentControl'];
            this.notes = _data['Notes'];
            this.type = _data['Type'];
        }
    }

    static fromJS(data: any): Control {
        data = typeof data === 'object' ? data : {};
        let result = new Control();
        result.init(data);
        return result;
    }
}

export class iApplyFormControl extends Control implements IFormControl {
    orderPosition: number = 0;

    // properties for ise in the workbench.
    readonly isValueControl: boolean = false;
    readonly isInputControl: boolean = false;
    readonly isOptionControl: boolean = false;
    readonly isControlGroup: boolean = false;
    readonly isMobileInput: boolean = false;
    readonly isValueWithMetadata: boolean = false;

    isHidden: boolean = false;
    valueTemplate?: IApplicationDataObject;
    wantsVisible?: boolean;
    masterControl?: string;

    lastIsVisibleValue: boolean = undefined;
    private _isVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    constructor(data?: IControl) {
        super(data);
    }

    get isReadOnly(): boolean {
        return this.access !== AccessLevel.Write;
    }

    get isVisible$(): Observable<boolean> {
        return this._isVisible.asObservable();
    }

    updateIsVisible(flag: boolean) {
        this.lastIsVisibleValue = flag;
        this._isVisible.next(flag);
    }

    // ALL OTHER PROPERTIES...
    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            // Add remaining <any> data properties
            for (let property in _data) {
                trySetProp(this, _data, property);
            }

            if (_data['ParentControl']) {
                // Special mapping case.
                this.parentControlId = _data['ParentControl'];
            }

            this.visibilityRule = _data['VisibilityRule']
                ? (new VisibilityTruthCondition()).init(_data['VisibilityRule'])
                : <any>undefined;

            this.hasVisibilityRule = !!this.visibilityRule?.value;
        }

        this.isHidden = this.isHidden == true || this.type === ControlType.Hidden;

        return this;
    }

    static fromJS(data: any): iApplyFormControl {
        data = typeof data === 'object' ? data : {};
        // let result = new Control();
        let result = ControlFactory.createControl(data);
        result.init(data);
        return result;
    }
}

export class ContainerControl extends iApplyFormControl implements
    IContainerControl,
    IControlWithLabel {
    type: ControlType.Group | ControlType.Repeater;
    readonly isControlGroup: boolean = true;

    label: string;
    controls: iApplyFormControl[] = [];

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            trySetProp(this, _data, 'Label');
            // this.controls = _data["Controls"] ? _data["Controls"].map(iApplyFormControl.fromJS) : [];
            if (Array.isArray(_data['Controls'])) {
                this.controls = _data['Controls'].map(iApplyFormControl.fromJS);
            }
        }

        return this;
    }
}

// Barcodescanner = "barcodescanner",
export class BarcodeScanner extends iApplyFormControl implements
    IValueControl,
    IControlWithToolTip,
    IControlWithPlaceholder,
    IControlWithValidators,
    IControlWithBarCode,
    IValuePersistableControl {
    readonly type: ControlType = ControlType.Barcodescanner;
    readonly isValueControl: boolean = true;

    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    tooltip: string = '';

    placeholder: string = '';
    validators: ControlRegexValidator[] = [];

    //barcodeScanner specific
    persistValue: boolean = true;
    navigateOnSuccess: boolean = false;
    barcodeType: BarcodeType = BarcodeType.QRCode;
    scanWidth: number | null = null;
    scanHeight: number | null = null;
    scanBarcodeLabel: string = '';
    displayValue: boolean = false;
}

// Calculation = "calculation",
export class CalculationControl extends iApplyFormControl implements
    IControl,
    IControlWithLabel,
    IControlWithToolTip,
    IControlWithSize,
    IValuePersistableControl {
    readonly type: ControlType = ControlType.Calculation;
    readonly isValueControl: boolean = true;
    // readonly isCalculationControl: boolean = true;

    get isReadOnly(): boolean {
        return true;
    }

    label: string = '';
    tooltip: string = '';
    // IControlWithSize
    size: ControlSize = ControlSize.Medium;
    floating: boolean = false;

    //Calculation specific
    formatAsCurrency: boolean = false;
    calculationExpression: string = '';
    isHidden: boolean = false;

    // IValuePersistableControl
    persistValue: boolean = false;
    persistValueLabel: string = 'Hide On-Screen';
}

export class CheckboxControl extends iApplyFormControl implements
    IControlWithLabel,
    IValueControl,
    IControlWithDefaultValue,
    IControlPrePopulate,
    IControlWithToolTip,
    IValuePersistableControl {
    readonly type: ControlType = ControlType.Checkbox;
    readonly isValueControl: boolean = true;

    // IValueControl
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';

    // IControlPrePopulate
    allowPrePopulate: boolean = false;
    // IControlWithToolTip
    tooltip: string = '';

    // IValuePersistableControl
    persistValue: boolean = false;

    // IIndexableControl,
    isIndexedField: boolean = false;

    // IControlWithDefaultValue,
    defaultValue: string = '';
    value: string = 'checked';
}

// Checkboxgroup = "checkboxgroup",
export class CheckboxGroupControl extends iApplyFormControl implements
    IControlWithLabel,
    IValueControl,
    IControlWithObjectData,
    IControlWithDefaultValue,
    IControlWithOptions,
    IControlWithToolTip,
    IValuePersistableControl,
    IControlPrePopulate {
    readonly type: ControlType = ControlType.Checkboxgroup;
    readonly isValueControl: boolean = true;
    readonly isOptionControl: boolean = true;

    // IValueControl
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    // IControlWithObjectData
    valueTemplate: IApplicationDataObject = new ApplicationDataArray();
    // IControlPrePopulate
    allowPrePopulate: boolean = false;

    // IControlWithDefaultValue,
    defaultValue: string = '';

    // IControlWithOptions
    optionFilterControl: string = '';
    allowMultipleSelect: boolean = true;
    optionSourceType: OptionSourceType = OptionSourceType.Static;
    optionSource: IOptionSource = new StaticOptionSource();
    options: ControlValueOption[] = [];

    // IValuePersistableControl
    persistValue: boolean = false;

    // ICarouselControl
    displayAsCarousel: boolean = false;
    carouselStyle: CarouselStyle;

    // Other
    useAutocomplete: boolean = false;

    // IControlWithToolTip,
    tooltip: string = '';

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            initControlWithOptions(this, _data);
        }

        return this;
    }
}

// Combo = "combo",
export class ComboControl extends iApplyFormControl implements
    IValueControl,
    IControlWithObjectData,
    IControlWithDefaultValue,
    IControlWithOptions,
    IControlWithToolTip,
    IIndexableControl,
    IValuePersistableControl,
    IControlPrePopulate,
    IControlWithSize {
    readonly type: ControlType = ControlType.Combo;
    readonly isValueControl: boolean = true;
    readonly isOptionControl: boolean = true;

    // IValueControl
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    // IControlWithObjectData
    valueTemplate: IApplicationDataObject = new ApplicationDataObject();
    // IControlPrePopulate
    allowPrePopulate: boolean = false;
    // IControlWithDefaultValue,
    defaultValue: string = '';
    // IControlWithOptions
    optionFilterControl: string = '';
    allowMultipleSelect: boolean = false;
    rows: number = 1; // only applies when allowMultipleSelect == true
    useAutocomplete: boolean = false;
    useCloudSearch: boolean = false;

    optionSourceType: OptionSourceType = OptionSourceType.Static;
    optionSource: IOptionSource = new StaticOptionSource();
    options: ControlValueOption[] = [];
    // IIndexableControl,
    isIndexedField: boolean = false;
    // IValuePersistableControl
    persistValue: boolean = false;
    // IControlWithSize
    size: ControlSize = ControlSize.Medium;
    // IControlWithToolTip,
    tooltip: string = '';

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            initControlWithOptions(this, _data);
        }

        if (this.allowMultipleSelect) {
            this.valueTemplate = new ApplicationDataArray();
        }

        return this;
    }
}

// Creditcard = "creditcard",
export class CreditcardControl extends iApplyFormControl implements
    IControlWithLabel,
    IValueControl,
    IControlPrePopulate {
    readonly type: ControlType = ControlType.Creditcard;
    readonly isValueControl: boolean = true;

    mandatory: boolean = false;
    mandatoryMessage: string | undefined = '';
    // IControlPrePopulate
    allowPrePopulate: boolean = false;

    label: string = '';

    //Credit card specific
    amountControlName: string = '';
    alwaysSubmit: boolean = false;
    requiresRedirection: boolean = false;
    paymentGateway: PaymentGateway;
    system: PaymentGatewayEnvironment;
}

export class CybersourceControl extends iApplyFormControl implements
    IControlWithLabel,
    IValueControl,
    IValuePersistableControl {
    readonly type: ControlType = ControlType.Cybersource;
    readonly persistValue: boolean = true;

    // IValueControl
    label: string = '';
    readonly mandatory: boolean = false;
    readonly mandatoryMessage: string = '';

    // ICyberesourceControl...
    content: string = '';
    buttonCaption: string;
    showButtonIcon: boolean;
    paymentSuccessText: string;
    amountControlName: string;
    amountValueFormat: AmountValueFormat;
    autoRedirect: boolean;
    submitOnSuccess: boolean;
    cybersourceEnvironment: CybersourceEnvironment;

    // TODO: Confirm Not required in front-end...?
    // CustomerReferenceControl
    // TransactionReferenceControl
    // TransactionRequest
    // TransactionResponse

    // init(_data?: any): this {
    //     super.init(_data);
    //     return this;
    // }
}

// Datepicker = "datepicker",
export class DateControl extends iApplyFormControl implements
    IValueControl,
    IControlWithDefaultValue,
    IIndexableControl,
    IControlWithToolTip,
    IControlWithPlaceholder,
    IControlWithDates,
    IValuePersistableControl {
    readonly type: ControlType = ControlType.Datepicker;
    readonly isValueControl: boolean = true;

    // IValueControl
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    // IControlWithDefaultValue,
    defaultValue: string = '';
    // IIndexableControl,
    isIndexedField: boolean = false;
    // IControlWithToolTip,
    tooltip: string = '';
    // IControlWithPlaceholder
    placeholder: string = '';
    // IValuePersistableControl
    persistValue: boolean = false;
    persistValueLabel: string = 'Hide On-Screen';

    //IControlWithDates
    minDate: string = '';
    maxDate: string = '';
}

// Docgen = "docgen",
export class DocGenControl extends iApplyFormControl implements
    IControlWithDefaultValue,
    IValueControl {
    readonly type: ControlType = ControlType.Docgen;
    // IValueControl
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    defaultValue: string = '';

    //docgen specific
    content: string = '';
    documentTemplateFile: string = '';
    documentName: string = '';
    regenerate: boolean = false;
    appendUploadedFiles: boolean = false;
    renderRole: string = '';
    fileNameUploaded: string = '';
}

// Filebrowser = "filebrowser",
export class FileBrowserControl extends iApplyFormControl implements
    IValueControl,
    IControlWithLabel,
    IControlWithToolTip,
    IFileBrowserControl,
    IValuePersistableControl {
    readonly type: ControlType = ControlType.Filebrowser;
    readonly isValueControl: boolean = true;

    // IValueControl, IControlWithLabel
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';

    // IValuePersistableControl
    persistValue: boolean = false;

    // IFileBrowserControl control specific
    allowedFileTypes: FileType[] = [];
    maximumFileSizePerFile: number | null = 10;
    maximumFileUploadCount: number | null = 1;

    // IControlWithToolTip
    tooltip: string = '';

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            if (Array.isArray(_data['AllowedFileTypes'])) {
                this.allowedFileTypes = _data['AllowedFileTypes'].map(val => FileType.fromJS(val));
            }
        }

        return this;
    }

    static fromJS(data: any): FileBrowserControl {
        data = typeof data === 'object' ? data : {};
        return (new FileBrowserControl()).init(data);
    }
}

// Geolocation = "geolocation",
export class GeoLocationControl extends iApplyFormControl implements
    IValueControl,
    IControlWithToolTip,
    IControlGeoLocation {
    readonly type: ControlType = ControlType.Geolocation;
    readonly isValueControl: boolean = true;

    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    tooltip: string = '';
    displayStyleGeolocation: GeolocationDisplayStyle;

    //geolocation specific
    displayStyle: GeolocationDisplayStyle;
}

export class GroupControl extends ContainerControl {
    readonly type = ControlType.Group;

    // FULL-SCREEN style specific Properties...
    // The background image to fill the Control Group background.
    backgroundImage: string = null;
    // The background color to fill the Control Group background.
    backgroundColor: string = null;
    // The font color of the Control Group.
    fontColor: string = null;
    // The label for button to drive the navigation to the next Control Group.
    buttonLabel: string = null;
}

// Heading = "heading",
export class HeadingControl extends iApplyFormControl {
    readonly type: ControlType = ControlType.Heading;

    //heading specific (default as Heading 1)
    level: number = 1;
    content: string = '';
}

// Hidden = "hidden",
export class HiddenControl extends iApplyFormControl implements
    IControlWithDefaultValue,
    IIndexableControl,
    IControlPrePopulate,
    IValuePersistableControl {
    readonly type: ControlType = ControlType.Hidden;
    readonly isValueControl: boolean = true;

    label: string = '';
    // IControlPrePopulate
    allowPrePopulate: boolean = false;
    defaultValue: string = '';
    isIndexedField: boolean = false;

    // IValuePersistableControl
    persistValue: boolean = false;
}

export class HtmlControl extends iApplyFormControl {
    readonly type: ControlType = ControlType.Html;
    content: string = '';
}

// Label = "label",
export class LabelControl extends iApplyFormControl {
    //LAbel specific
    content: string = '';
}

// Likert = "likert",
export class LikertControl extends iApplyFormControl implements
    IValueControl,
    IControlWithToolTip {
    readonly type: ControlType = ControlType.Likert;
    readonly isValueControl: boolean = true;
    readonly isLikertControl: boolean = true;

    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    // IControlWithTooltip
    tooltip: string = '';

    //likert specific
    scale: Array<LikertScaleItem> = [];
    questions: Array<ControlSubField> = [];

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            if (Array.isArray(_data["Questions"])) {
                this.questions = _data['Questions'].map(ControlSubField.fromJS);
            }

            if (Array.isArray(_data["Scale"])) {
                this.scale = _data['Scale'].map(LikertScaleItem.fromJS);
            }
        }

        return this;
    }

}

// Photobooth = "photobooth",
export class PhotoboothControl extends iApplyFormControl implements
    IValueControl,
    IControlWithToolTip,
    IValuePersistableControl,
    IControlPrePopulate {
    readonly type: ControlType = ControlType.Photobooth;
    readonly isValueControl: boolean = true;

    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';

    // IControlPrePopulate
    allowPrePopulate: boolean = false;
    tooltip: string = '';
    persistValue: boolean = false;

    //photobooth specific
    photoSize: PhotoSize = PhotoSize.Medium;
    orientation: PhotoOrientation = PhotoOrientation.Landscape;
    takePhotoLabel: string = '';
    photoFillColour: string = '';
}

// Radio = "radio",
export class RadioControl extends iApplyFormControl implements
    IValueControl,
    IControlWithLabel,
    IControlWithObjectData,
    IControlWithOptions,
    IControlWithDefaultValue,
    IControlWithToolTip,
    IValuePersistableControl,
    ICarouselControl,
    IControlPrePopulate {
    readonly type: ControlType = ControlType.Radio;
    readonly isValueControl: boolean = true;
    readonly isOptionControl: boolean = true;
    readonly isValueWithMetadata: boolean = true;

    // IValueControl, IControlWithLabel
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    // IControlWithObjectData
    valueTemplate: IApplicationDataObject = new ApplicationDataObject();
    // IControlPrePopulate
    allowPrePopulate: boolean = false;

    // IControlWithOptions
    optionFilterControl: string = '';
    allowMultipleSelect: boolean = false;
    optionSourceType: OptionSourceType = OptionSourceType.Static;
    optionSource: IOptionSource = new StaticOptionSource();
    options: ControlValueOption[] = [];

    // IControlWithDefaultValue,
    defaultValue: string = '';

    // IValuePersistableControl
    persistValue: boolean = false;

    // ICarouselControl
    displayAsCarousel: boolean = false;
    carouselStyle: CarouselStyle = CarouselStyle.CenterMode;

    // IControlWithToolTip,
    tooltip: string = '';

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            initControlWithOptions(this, _data);
            // trySetProp(this, _data, "Label");
            // trySetProp(this, _data, "Mandatory");
            // trySetProp(this, _data, "MandatoryMessage");
            // trySetProp(this, _data, "RadioDefaultValue");
            // trySetProp(this, _data, "RadioPersistValue");
            // trySetProp(this, _data, "RadioDisplayAsCarousel");
            // trySetProp(this, _data, "RadioCarouselStyle");
        }

        return this;
    }
}

export class RepeaterControl extends ContainerControl {
    readonly type = ControlType.Repeater;

    addRowButtonLabel: string = 'Add row';
    displayStyle: RepeaterDisplayStyle = RepeaterDisplayStyle.Table;
    displayRowNumber: boolean = false;
    masterControl: string = '';
    masterRepeater: RepeaterControl | undefined;
    maximumCount: number = -1;
    maximumCountField: string | undefined;
    maximumCountSource: MapType = MapType.Value;
    minimumCount: number = -1;
    minimumCountField: string | undefined;
    minimumCountSource: MapType = MapType.Value;
    preValidate: boolean = false;
    slaveControls: string[] = [];
    summaryFields: string[] = [];
    totalFields: string[] = [];
    uniqueFields: string[] = [];
    uniqueValidationMessage: string = '';

    init(_data?: any): this {
        super.init(_data);
        if (_data['MasterRepeater']) {
            this.masterRepeater = (new RepeaterControl).init(_data['MasterRepeater']);
        }

        return this;
    }
}

// Search = "search",
export class SearchControl extends iApplyFormControl implements
    ISearchControl,
    IControlWithLabel,
    IControlWithPlaceholder,
    IControlWithValidators {
    readonly type: ControlType = ControlType.Search;
    readonly isInputControl: boolean = true;
    readonly isValueControl: boolean = true;

    label: string = '';
    placeholder: string = '';
    validators: ControlRegexValidator[] = [];

    //search specific
    retainValue: boolean = false;
    staticOption: boolean = false;
    staticOptionMessage: string = '';
    searchDisplayField: string = '';
    autoSelectSingleResult: boolean = false;
    manualSearch: boolean = false;
    floating: boolean = false;
    minimumInputLength: number = 4;
    triggerSaveEvent: boolean = false;
    searchAgainAndClear: boolean = false;

    // to be completed
    searchSource: ServiceEndpointUtilisation = new ServiceEndpointUtilisation();

    init(_data?: any): this {
        super.init(_data);
        if (_data) {
            if (_data['SearchSource']) {
                this.searchSource = ServiceEndpointUtilisation.fromJS(_data['SearchSource']);
            }
        }
        if (Array.isArray(_data['Validators'])) {
            this.validators = _data['Validators'].map(item => ControlRegexValidator.fromJS(item));
        }
        return this;
    }
}

// Signaturepad = "signaturepad",
export class SignaturePadControl extends iApplyFormControl implements
    IControlWithToolTip,
    IValueControl {
    readonly type: ControlType = ControlType.Signaturepad;
    readonly isValueControl: boolean = true;
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    tooltip: string = '';
}

// Summary = "summary",
export class SummaryControl extends iApplyFormControl implements
    iApplyFormControl {
    readonly type: ControlType = ControlType.Summary;

    //summary specific
    defaultPermissionsTo: DefaultPermissionsTo;
    displaySurveyResult: boolean = false;
}

export class TextControl extends iApplyFormControl implements
    IControlWithRows,
    IControlWithDefaultValue,
    IIndexableControl,
    IControlWithToolTip,
    IControlWithPlaceholder,
    IValuePersistableControl,
    IControlWithValidators,
    IValueControl,
    IControlPrePopulate,
    IControlWithSize {
    readonly type: ControlType = ControlType.Text;
    readonly isValueControl: boolean = true;
    readonly isInputControl: boolean = true;
    readonly isMobileInput: boolean = true;

    // IControlWithDefaultValue
    defaultValue: string = '';
    // IControlWithPlaceholder
    placeholder: string = '';
    // IControlWithRows
    rows: number = 1;
    // IControlWithToolTip
    tooltip: string = '';
    // IControlWithValidators
    validators: ControlRegexValidator[] = [];
    // IIndexableControl
    isIndexedField: boolean = false;
    // IValueControl, IControlWithLabel
    label: string = '';
    mandatory: boolean = false;
    mandatoryMessage: string = '';
    // IControlPrePopulate
    allowPrePopulate: boolean = false;
    //IValuePersistableControl
    persistValue: boolean = false;

    // TEXT control specific
    maxLength: number | null = null;
    size: ControlSize = ControlSize.Medium;
    inputType: InputType | null = null;
    mobileKeyboardOptions: MobileKeyboardOptions = new MobileKeyboardOptions();
    maxlength: number | null = null;

    init(_data?: any): this {
        super.init(_data);

        if (_data) {
            // Add remaining custom control-specific properties
            if (Array.isArray(_data['Validators'])) {
                this.validators = _data['Validators'].map(item => ControlRegexValidator.fromJS(item));
            }

            if (_data['MobileKeyboardOptions']) {
                this.mobileKeyboardOptions = MobileKeyboardOptions.fromJS(_data['MobileKeyboardOptions']);
            }
        }

        return this;
    }
}

// Timepicker = "timepicker",
export class TimepickerControl extends iApplyFormControl implements
    IValueControl,
    IControlWithDefaultValue,
    IIndexableControl,
    IControlWithToolTip,
    IControlWithPlaceholder,
    IControlPrePopulate {
    readonly type: ControlType = ControlType.Timepicker;
    readonly isValueControl: boolean = true;

    label: string | undefined;
    mandatory: boolean;
    mandatoryMessage: string | undefined;
    // IControlWithDefaultValue
    defaultValue: string = '';
    // IIndexableControl
    isIndexedField: boolean = false;
    // IControlWithToolTip
    tooltip: string = '';
    // IControlWithPlaceholder
    placeholder: string = '';
    // IControlPrePopulate
    allowPrePopulate = false;

    //timepicker specific
    Floating: boolean = false;
    minuteIncrement: number | null = null;
}

// Twofactorverification = "twofactorverification",
export class TwoFactorVerificationControl extends iApplyFormControl implements
    IValueControl,
    IControlPrePopulate {
    readonly type: ControlType = ControlType.Twofactorverification;
    readonly isValueControl: boolean = true;

    label: string | undefined;
    mandatory: boolean;
    mandatoryMessage: string | undefined;
    // IControlPrePopulate
    allowPrePopulate: boolean = false;
    //twofactor specific
    credentialsId: string = '';
    senderId: string = '';
    textMessage: string = '';
    maxRetryCount: number = 0;
    timeout: number;
    otpExpiresIn: number;
    otpCodeLength: number;
}

export class DigitalIdControl extends iApplyFormControl implements
    IValueControl,
    IControlWithLabel
{
    readonly type: ControlType = ControlType.DigitalId;
    readonly isValueControl: boolean = false;

    label: string | undefined;
    mandatory: boolean;
    mandatoryMessage: string | undefined;

    content?: string;

    openIdClientId: string;
    openIdClientSecret: string;
    acrValues: string = "urn:id.gov.au:tdif:acr:ip1:cl3";
    identityProofingLevel: string = "ip1";
    credentialLevel: string = "cl1";
    identityScope: string[] = [ "openid", "profile", "email" ];
    autoRedirect: boolean = false;
    submitOnValidResponse: boolean = true;
    buttonCaption: string = "Verify Now";
    showButtonIcon: boolean = true
    identityIncompleteText: string = "Your identity has not yet been verified";
    identityCompleteText: string = "Your identity has been successfully verified";
    responseFieldMap: MappedField[] = [];
}


export class SimpleControl {
    name!: string;
    label!: string | undefined;
    type!: ControlType;
}


export const initControlWithOptions = (control: iApplyFormControl, _data?: any) => {
    if (isControlWithOptions(control)) {
        // optionFilterControl: string = '';
        trySetProp(control, _data, 'OptionFilterControl');

        // allowMultipleSelect: boolean = true;
        trySetProp(control, _data, 'AllowMultipleSelect');

        // optionSourceType: OptionSourceType = OptionSourceType.Static;
        trySetProp(control, _data, 'OptionSourceType');

        // optionSource: IOptionSource = new StaticOptionSource();
        // trySetProp(control, _data, 'OptionSource');
        if (_data['OptionSource']['Type']) {
            control.optionSourceType = _data['OptionSource']['Type']
        }

        switch (control.optionSourceType) {
            case OptionSourceType.Static:
            case OptionSourceType.Prebuilt:
            case OptionSourceType.System:
                control.optionSource = StaticOptionSource.fromJS(_data['OptionSource']);
                break;
            case OptionSourceType.Dynamic:
                control.optionSource = DynamicOptionSource.fromJS(_data['OptionSource']);
                break;
            case OptionSourceType.Repeater:
                control.optionSource = RepeaterOptionSource.fromJS(_data['OptionSource']);
                break;
            default:
                break;
        }

        // options: ControlValueOption[] = [];
        if (Array.isArray(_data['Options'])) {
            // control.options = [];
            control.options = _data['Options'].map((item) => ControlValueOption.fromJS(item));
        } else if (control?.optionSource?.options?.length) {
            control.options = control?.optionSource?.options;
        }
    }
};
